class ComprehensiveCartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.weightQuantity = this.parseWeight(this.dataset.weightQuantity, 0.3);
    this.weightFrequency = this.parseWeight(this.dataset.weightFrequency, 0.4);
    this.weightPosition = this.parseWeight(this.dataset.weightPosition, 0.3);
  }

  parseWeight(value, defaultValue) {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  }

  connectedCallback() {
    try {
      const cartItemsString = this.dataset.cartItems.replace(/&quot;/g, '"');
      this.cartItems = JSON.parse(cartItemsString);
      this.combinedCartItems = this.combineVariants(this.cartItems);
      this.maxPosition = parseInt(this.dataset.maxPosition, 10) || 4;
      this.productsToShow = parseInt(this.dataset.productsToShow, 10) || this.maxPosition;
    } catch (e) {
      this.cartItems = [];
      this.combinedCartItems = [];
      this.maxPosition = 4;
      this.productsToShow = 4;
    }

    if (this.combinedCartItems.length > 0) {
      this.initializeRecommendations();
    }
  }

  combineVariants(cartItems) {
    const combinedItems = {};
    cartItems.forEach(item => {
      const productId = item.product_id;
      if (combinedItems[productId]) {
        combinedItems[productId].quantity += item.quantity;
      } else {
        combinedItems[productId] = { ...item };
      }
    });
    return Object.values(combinedItems);
  }

  initializeRecommendations() {
    this.loadRecommendations();
  }

  loadRecommendations() {
    const productIds = this.combinedCartItems.map(item => item.product_id);
    const recommendationPromises = productIds.map(id => this.fetchRecommendationsForProduct(id));
    
    Promise.all(recommendationPromises)
      .then(allRecommendations => {
        const processedRecommendations = this.processRecommendations(allRecommendations, productIds);
        this.renderRecommendations(processedRecommendations);
      })
      .catch(error => {
        console.error('Error loading recommendations:', error);
      });
  }

  async fetchRecommendationsForProduct(productId) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`;
    try {
      const response = await fetch(url);
      const text = await response.text();
      const html = document.createElement('div');
      html.innerHTML = text;
      const productCards = html.querySelectorAll('.cart-drawer__recommendations .grid__item');
      return Array.from(productCards).map(card => this.extractProductData(card));
    } catch (error) {
      console.error(`Error fetching recommendations for product ${productId}:`, error);
      return [];
    }
  }

  extractProductData(card) {
    const productInfoElement = card.querySelector('product-info-cart-upsell');
    if (productInfoElement) {
      const productId = productInfoElement.dataset.productId;
      return {
        id: productId,
        element: card.cloneNode(true)
      };
    }
    return null;
  }

  processRecommendations(allRecommendations, sourceProductIds) {
    const totalCartQuantity = this.combinedCartItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalCartItems = this.combinedCartItems.length;
    const maxPosition = this.maxPosition;

    const processedRecommendations = [];
    allRecommendations.forEach((recommendations, index) => {
      const sourceProductId = sourceProductIds[index];
      const sourceCartItem = this.combinedCartItems.find(item => item.product_id === sourceProductId);
      const sourceQuantity = sourceCartItem ? sourceCartItem.quantity : 1;

      recommendations.forEach((rec, position) => {
        if (!rec) return;
        const existingRec = processedRecommendations.find(r => r.id === rec.id);
        const invertedPosition = maxPosition + 1 - (position + 1);

        if (existingRec) {
          existingRec.count++;
          existingRec.recommendedBy.push({
            productId: sourceProductId,
            position: position + 1,
            quantity: sourceQuantity,
            invertedPosition: invertedPosition
          });
        } else {
          processedRecommendations.push({
            ...rec,
            count: 1,
            recommendedBy: [{
              productId: sourceProductId,
              position: position + 1,
              quantity: sourceQuantity,
              invertedPosition: invertedPosition
            }],
            originalPosition: null
          });
        }
      });
    });

    processedRecommendations.forEach((rec, index) => {
      rec.originalPosition = index + 1;
      rec.totalQuantity = rec.recommendedBy.reduce((sum, item) => sum + item.quantity, 0);
      rec.timesRecommended = rec.count;
      rec.avgInvertedPosition = rec.recommendedBy.reduce((sum, item) => sum + item.invertedPosition, 0) / rec.recommendedBy.length;

      const { score } = this.calculateScore(rec, totalCartQuantity, totalCartItems, maxPosition);
      rec.score = score;
    });

    return processedRecommendations.sort((a, b) => b.score - a.score);
  }

  calculateScore(recommendation, totalCartQuantity, totalCartItems, maxPosition) {
    const Q_p_norm = recommendation.totalQuantity / totalCartQuantity;
    const N_p_norm = recommendation.timesRecommended / totalCartItems;
    const P_p_norm = recommendation.avgInvertedPosition / maxPosition;

    const score = (this.weightQuantity * Q_p_norm) + 
                 (this.weightFrequency * N_p_norm) + 
                 (this.weightPosition * P_p_norm);

    return { score };
  }

  renderRecommendations(recommendations) {
    const limitedRecommendations = recommendations.slice(0, this.productsToShow);
    
    const container = document.createElement('div');
    container.className = 'product-recommendations no-markers';
    
    limitedRecommendations.forEach((item) => {
      if (item && item.element) {
        container.appendChild(item.element);
      }
    });
    
    this.innerHTML = '';
    this.appendChild(container);
    
    const recommendationsContainer = this.closest('.cart-drawer__recommendations');
    if (recommendationsContainer) {
      recommendationsContainer.classList.toggle('has-recommendations', limitedRecommendations.length > 0);
    }
    
    this.classList.toggle('product-recommendations--loaded', limitedRecommendations.length > 0);

    // After rendering, initialize the cart upsell handlers
    this.dispatchEvent(new CustomEvent('cart-recommendations-rendered', { bubbles: true }));
  }
}

customElements.define('comprehensive-cart-recommendations', ComprehensiveCartRecommendations);